import { Component, OnInit } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { PrFormConstructor } from '@app/components/pr-form/pr-form.contructor';
import { ReferenceDao } from '@app/dao/reference/reference.dao';
import { isSaleViaAlbR } from '@app/forms/subscription/definition/constants/organization.constants';
import { PledgeRead } from '@app/forms/subscription/definition/interface/Pledge';
import { SubscriptionFacadeService } from '@app/forms/subscription/services/subscription-facade.service';
import { SupportsTable } from '@app/forms/subscription/ea-subscription-forms/operations-form/shared/supports-table/supports-table.model';
import { EaSubscriptionBuilder } from './ea-subscription-form.builder';

// PRE-4896/PRE-4906 : les 4 champs "*Saisi" ci-dessous sont désactivés (readonly) pour toute vente
// via AlbR (organisation affiliée EUODIA, ou produit sans vente en direct) — cf. applyReadonlyState()
// et isSaleViaAlbR().
const SAISI_CONTROL_NAMES = [
  'upfrontAssureurSaisi',
  'upfrontEmetteurSaisi',
  'fraisGestionSaisi',
  'retrocessionAssureurSaisi',
];

@UntilDestroy()
@Component({
  selector: 'taux-par-support-transfer',
  templateUrl: './taux-par-support-transfer.component.html',
  styleUrls: ['./taux-par-support-transfer.component.scss'],
  standalone: false,
})
export class TauxParSupportTransferComponent
  extends EaSubscriptionBuilder
  implements OnInit
{
  pledge: PledgeRead;
  isReadonly = false;
  // Config des 2 <supports-table> (même composant/design que isin-table) : le même FormArray
  // supportRepartition alimente les deux, séparées par rowFilter puisque UC/Fonds Euros et produits
  // structurés n'exposent pas les mêmes taux.
  nonStructuredTable: SupportsTable;
  structuredTable: SupportsTable;
  private supportTypeLabels: Record<string, string> = {};

  constructor(
    protected formConstructor: PrFormConstructor,
    private subscriptionFacade: SubscriptionFacadeService,
    private referenceDao: ReferenceDao,
  ) {
    super(formConstructor);
  }

  ngOnInit() {
    this.pledge = this.subscriptionFacade.getPledge();
    this.subscriptionFacade.initForm(this);

    // PRE-4896/PRE-4906 : même source que le titre/sous-titre de la slide (getTitle/getSubTitle dans
    // financial-knowledge-qustions.service.ts), pour éviter toute divergence entre le libellé
    // affiché et le comportement réel des champs. user.organizationId est déjà disponible de façon
    // synchrone (BehaviorSubject alimenté à l'authentification, avant l'accès au funnel).
    this.isReadonly = isSaleViaAlbR(
      this.subscriptionFacade.getUser()?.organizationId,
      this.pledge?.products,
    );
    this.applyReadonlyState();
    this.buildTables();

    this.referenceDao
      .getSupportTypes()
      .pipe(untilDestroyed(this))
      .subscribe(types => {
        this.supportTypeLabels = Object.fromEntries(
          (types || []).map((type: any) => [type.id, type.value]),
        );
      });
  }

  getSupportTypeLabel(typeId: string): string {
    return this.supportTypeLabels[typeId] || typeId;
  }

  private buildTables() {
    const getTypeLabel = this.getSupportTypeLabel.bind(this);
    const supportColumn = {
      label: 'Support',
      type: 'support' as const,
      nameControlName: 'supportName',
      typeControlName: 'supportType',
    };

    this.nonStructuredTable = {
      title: 'UC & Fonds Euros',
      removable: false,
      emptyLabel: 'Aucun support UC/Fonds Euros dans ce transfert.',
      getTypeLabel,
      rowFilter: repartition => !this.isStructuredProduct(repartition),
      columns: [
        supportColumn,
        {
          label: 'Frais de gestion',
          type: 'input',
          controlName: 'fraisGestionSaisi',
          unit: '%',
          numeric: true,
          ariaLabel: 'Frais de gestion support',
          warnWhenEmpty: true,
        },
        {
          label: 'Rétrocession assureur',
          type: 'input',
          controlName: 'retrocessionAssureurSaisi',
          unit: '%',
          numeric: true,
          ariaLabel: 'Rétrocession assureur',
          warnWhenEmpty: true,
        },
      ],
    };

    this.structuredTable = {
      title: 'Produits structurés',
      removable: false,
      emptyLabel: 'Aucun produit structuré dans ce transfert.',
      getTypeLabel,
      rowFilter: repartition => this.isStructuredProduct(repartition),
      columns: [
        supportColumn,
        {
          label: 'Upfront assureur',
          type: 'input',
          controlName: 'upfrontAssureurSaisi',
          unit: '%',
          numeric: true,
          ariaLabel: 'Upfront assureur',
          warnWhenEmpty: true,
        },
        {
          label: 'Upfront émetteur',
          type: 'input',
          controlName: 'upfrontEmetteurSaisi',
          unit: '%',
          numeric: true,
          ariaLabel: 'Upfront émetteur',
          warnWhenEmpty: true,
        },
      ],
    };
  }

  // Champs concernés par rowFilter/warnWhenEmpty : uniquement les 2 pertinents pour le type de
  // support (UC/Fonds Euros vs produit structuré), pas les 4 à la fois — un produit structuré n'a
  // jamais de fraisGestionSaisi/retrocessionAssureurSaisi renseignés (cf. taux-par-support-transfer.component.html).
  private isRateEmpty(repartition: any, controlName: string): boolean {
    const value = repartition?.get(controlName)?.value;
    return value === null || value === undefined || value === '';
  }

  get hasEmptyRates(): boolean {
    return !!this.supportRepartition?.controls?.some(repartition => {
      const controlNames = this.isStructuredProduct(repartition)
        ? ['upfrontAssureurSaisi', 'upfrontEmetteurSaisi']
        : ['fraisGestionSaisi', 'retrocessionAssureurSaisi'];
      return controlNames.some(controlName =>
        this.isRateEmpty(repartition, controlName),
      );
    });
  }

  get emptyRatesWarning(): { display: string; icon: string; text: string } | null {
    if (!this.hasEmptyRates) return null;
    return {
      display: 'warning',
      icon: 'warning',
      text: this.isReadonly
        ? 'Veuillez contacter les équipes de gestion produit pour configurer les taux support manquant. Attention, le calcul des frais affichera 0 dans votre adéquation si vous continuez.'
        : 'Attention : le calcul des frais affichera 0 dans votre adéquation si vous ne saisissez rien.',
    };
  }

  isStructuredProduct(repartition: any): boolean {
    return repartition?.get('supportType')?.value === 'STRUCTURED_PRODUCTS';
  }

  get hasNonStructuredSupports(): boolean {
    return !!this.supportRepartition?.controls?.some(
      repartition => !this.isStructuredProduct(repartition),
    );
  }

  get hasStructuredSupports(): boolean {
    return !!this.supportRepartition?.controls?.some(repartition =>
      this.isStructuredProduct(repartition),
    );
  }

  private applyReadonlyState() {
    this.supportRepartition?.controls?.forEach(repartition => {
      SAISI_CONTROL_NAMES.forEach(controlName => {
        const control = repartition.get(controlName);
        if (!control) return;
        if (this.isReadonly) {
          control.disable();
        } else {
          control.enable();
        }
      });
    });
  }

  onSubmit() {
    this.form.markAsDirty();
    this.subscriptionFacade.selectErrorInputAndScroll(this);
    this.displayErrors();

    if (this.form.valid) {
      this.subscriptionFacade.submitPledge(this);
    }
  }

  prev() {
    this.subscriptionFacade.previousStep();
  }
}
